import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

const SRC = {
  teal: "/paint/teal.webp",
  teal2: "/paint/teal2.webp",
  teal3: "/paint/teal3.webp",
  orange: "/paint/orange.webp",
  orange2: "/paint/orange2.webp",
  orange3: "/paint/orange3.webp",
  coral: "/paint/coral.webp",
  coral2: "/paint/coral2.webp",
  coral3: "/paint/coral3.webp",
  swirl: "/paint/swirl.webp",
};

/**
 * Decorative paint-splatter accent (from the dog's palette) with gentle scroll
 * parallax. Outer motion.div owns the parallax translateY; the inner <img>
 * owns a static rotate/flip. No blur filters — GPU-safe.
 */
export const PaintSplat = ({
  color = "teal",
  className = "",
  rotate = 0,
  flip = false,
  opacity = 0.6,
  from = 60,
  to = -60,
}) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [from, to]);

  return (
    <motion.div
      ref={ref}
      aria-hidden
      style={{ y }}
      className={`absolute pointer-events-none z-0 select-none ${className}`}
    >
      {/* Entrance: flick/drip into place when the section scrolls into view */}
      <motion.div
        className="w-full h-full"
        initial={reduce ? false : { opacity: 0, scale: 0.62, rotate: -12, y: -22 }}
        whileInView={reduce ? undefined : { opacity: 1, scale: 1, rotate: 0, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{
          type: "spring",
          stiffness: 130,
          damping: 13,
          mass: 0.6,
          opacity: { duration: 0.55, ease: "easeOut" },
        }}
      >
        <div className="paint-splat-inner w-full h-full">
          <img
            src={SRC[color]}
            alt=""
            loading="lazy"
            draggable={false}
            className="w-full h-full object-contain"
            style={{
              opacity,
              transform: `rotate(${rotate}deg)${flip ? " scaleX(-1)" : ""}`,
            }}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};
